import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspAssets() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [managerAccountId, setManagerAccountId] = useState('201');

  function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  const submitBuild = async () => {
    if (newFiles.length > 0) {
      setSubmitting(true);
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
      const BATCH_SIZE = 25;

      const byAdvertiser = new Map();
      newFiles.forEach(item => {
        const id = item.advertiserId ?? ''; // fallback if needed
        if (!byAdvertiser.has(id)) byAdvertiser.set(id, []);
        byAdvertiser.get(id).push(item.fileName);
      })

      const batches = [];
      Array.from(byAdvertiser.entries()).flatMap(([advertiserId, names]) => chunk(names, BATCH_SIZE).forEach(group => {
        batches.push({ advertiserId, fileNames: group });
      }))

      const postBatch = async (batch, i, total, headers) => {
        const dspAssetBuildUrl = `${config.uploadUrl}/amz-dsp-assets/load`;
        const res = await fetch(dspAssetBuildUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            dspAccountId: batch.advertiserId,
            dspManagerAccountId: managerAccountId,
            fileNames: batch.fileNames,
          }),
        });
        if (!res.ok) throw new Error(`Batch ${i + 1}/${total} failed: ${res.status} ${res.statusText}`);
        // optional: const json = await res.json();
        Notify.show('primary', 'Success', `Batch ${i + 1} of ${total} assets built`);
      };

      await batches.reduce(
        (p, batch, i) => p.then(() => postBatch(batch, i, batches.length, headerWithAuth)),
        Promise.resolve(),
      );

      setForceRefresh(moment());
      setSubmitting(false);
    } else {
      Notify.show('danger', 'Error', 'Nothing set to BUILD');
    }
  };

  const submitProcessCreativeAndMediaPlan = async () => {
    setSubmitting(true);
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const dspAssetBuildUrl = `${config.serverUrl}/management/dsp/process/createive-and-media-plan?accountId=${selectedSeller.value}`;
    const res = await fetch(dspAssetBuildUrl, {
      method: 'GET',
      headers: headerWithAuth,
    });
    if (!res.ok) {
      Notify.show('danger', 'Error', `Process Creative and Media Plans Failed: ${res.status} ${res.statusText}`);
    } else {
      Notify.show('primary', 'Success', 'Process Creative and Media Plans complete');
    }
    setSubmitting(false);
  };

  const highlightChanges = (row) => {
    if (row.data?.asset_id === '') {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    { field: 'file_name', headerName: 'File Name', cellStyle: params => highlightChanges(params) },
    { field: 'dsp_account_id', headerName: 'DSP Advertiser ID', cellStyle: params => highlightChanges(params) },
    { field: 'manager_account_id', headerName: 'Manager Account ID', cellStyle: params => highlightChanges(params) },
    { field: 'asset_id', headerName: 'Asset ID', cellStyle: params => highlightChanges(params) },
    { field: 'version_id', headerName: 'Version ID', cellStyle: params => highlightChanges(params) },
    { field: 'process_ts.value', headerName: 'Process TS', cellStyle: params => highlightChanges(params) },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: false,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/assets`;

  const exportPrefix = getExportPrefix('amz-dsp-assets', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  useEffect(() => {
    const api = agGrid?.gridApi;
    if (!api) return undefined;

    const run = () => {
      const list = [];
      api.forEachNode((rowNode) => {
        if (rowNode.group) return;
        const d = rowNode.data;
        if (d && !d.asset_id) {
          list.push({ fileName: d.file_name, advertiserId: d.dsp_account_id });
        }
      });
      setNewFiles(list);
    };

    run();
    api.addEventListener('firstDataRendered', run);
    api.addEventListener('rowDataUpdated', run);

    const cleanup = () => {
      api.removeEventListener('firstDataRendered', run);
      api.removeEventListener('rowDataUpdated', run);
    };

    return cleanup;
  }, [agGrid?.gridApi]);


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
        />
      </Row>
      <Row>
        <CardPanel
          title={'Actions:'}
          autorenewButton={false}
          closeButton={false}
        >
          <Row>
            <Col md="3">
              <div style={{ width: '500px' }}>
                <p className="bold-text">{`Build Count: ${newFiles.length}`}</p>
                <p>Manager Account ID: <input type="text" className="input_basic" id="managerAccountId" value={managerAccountId} onChange={(e) => setManagerAccountId(e.target.value)} /></p>
                <span className="hint-text">201=Americas, 202=EMEA, 203=APAC</span>
                <br />
                <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitBuild()}>Build Assets</button>
              </div>
            </Col>
            <Col md="3">
              <div style={{ width: '500px' }}>
                <p className="bold-text">Kickoff Creative and Media Plan Process</p>
                <span className="hint-text">Will auto build Creatives and create Media Plans</span>
                <span className="hint-text">Requires Landing Page and Assets to be setup correctly</span>
                <br />
                <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitProcessCreativeAndMediaPlan()}>Process Creatives and Media Plans</button>
              </div>
            </Col>
          </Row>
        </CardPanel>
      </Row>
    </Container>
  );
}
